import { PalmettoIcon } from '@palmetto/dsm-components';
import { isHotkey } from 'is-hotkey';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createEditor, Editor, Element as SlateElement, type Descendant } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, Slate, withReact, type RenderPlaceholderProps } from 'slate-react';

import type { RichTextNode } from 'types';

import type { CustomElement, RichTextEditorProps } from '../../types/RichTextEditor';

import { insertLink, isBlockActive, toggleMark, unwrapLink, withLinks } from './editorCommands';
import { createEmptyValue, HOTKEYS, isEditorEmpty, normalizeValue } from './helpers';
import { LinkButton, ListButton, MarkButton, renderElement, renderLeaf } from './renderers';

export const RichTextEditor = ({ label, helperText, value, onChange, onBlur }: RichTextEditorProps) => {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const lastEmittedRef = useRef<string | null>(null);
  const latestValueRef = useRef(value);
  latestValueRef.current = value;

  const editor = useMemo(() => {
    const e = withLinks(withHistory(withReact(createEditor())));
    e.children = normalizeValue(latestValueRef.current);
    return e;
  }, []);

  const lastSyncedEditorRef = useRef(editor);

  const linkActive = isBlockActive(editor, 'link');

  const handleChange = useCallback(
    (val: Descendant[]) => {
      const result = isEditorEmpty(val) ? undefined : (val as unknown as RichTextNode[]);
      lastEmittedRef.current = JSON.stringify((result as Descendant[] | undefined) ?? createEmptyValue());
      onChange?.(result);
    },
    [onChange],
  );

  useEffect(() => {
    const incoming = normalizeValue(value);
    const incomingJson = JSON.stringify(incoming);
    const emptyJson = JSON.stringify(createEmptyValue());

    const editorChanged = editor !== lastSyncedEditorRef.current;
    lastSyncedEditorRef.current = editor;

    if (!editorChanged) {
      if (lastEmittedRef.current === emptyJson && incomingJson !== emptyJson) return;
      if (lastEmittedRef.current !== null && incomingJson === lastEmittedRef.current) return;
    }

    lastEmittedRef.current = incomingJson;

    if (JSON.stringify(editor.children) !== incomingJson) {
      editor.selection = null;
      editor.children = incoming;
      editor.onChange();
    }
  }, [editor, value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, e)) {
          e.preventDefault();
          toggleMark(editor, HOTKEYS[hotkey]);
        }
      }
    },
    [editor],
  );

  const validURL = useMemo(() => {
    if (!linkUrl) return true;
    try {
      const { protocol } = new URL(linkUrl);
      return protocol === 'http:' || protocol === 'https:';
    } catch {
      return false;
    }
  }, [linkUrl]);

  const openLinkDialog = useCallback(() => {
    const linkNode = Editor.nodes(editor, {
      match: (n) => SlateElement.isElement(n) && n.type === 'link',
    }).next().value;

    const url = linkNode ? ((linkNode[0] as CustomElement).url ?? '') : '';

    ReactEditor.blur(editor);

    setLinkUrl(url);
    setLinkDialogOpen(true);
  }, [editor]);

  const handleLinkButtonMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      openLinkDialog();
    },
    [openLinkDialog],
  );

  const applyLink = useCallback(() => {
    if (linkUrl && !validURL) return;
    if (linkUrl) {
      insertLink(editor, linkUrl);
    } else {
      unwrapLink(editor);
    }
    setLinkDialogOpen(false);
    setLinkUrl('');
    ReactEditor.focus(editor);
  }, [editor, linkUrl, validURL]);

  const removeLink = useCallback(() => {
    unwrapLink(editor);
    setLinkDialogOpen(false);
    setLinkUrl('');
    ReactEditor.focus(editor);
  }, [editor]);

  const handleLinkInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyLink();
      }
    },
    [applyLink],
  );

  const handleLinkUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLinkUrl(e.target.value);
  }, []);

  const closeLinkDialog = useCallback(() => {
    setLinkDialogOpen(false);
  }, []);

  const renderPlaceholder = useCallback(
    ({ attributes, children }: RenderPlaceholderProps) => (
      <span {...attributes} className="top-20!">
        {children}
      </span>
    ),
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      {label && <label className="text-sm">{label}</label>}
      <Slate editor={editor} initialValue={editor.children} onChange={handleChange}>
        <div className="overflow-hidden rounded-8 border border-border-default focus-within:border-border-focus">
          <div className="flex items-center gap-2 border-b border-border-default bg-canvas-subtle px-8 py-4">
            <MarkButton format="bold" label="B" />
            <MarkButton format="italic" label="I" />
            <div className="h-16 w-px bg-border-default" />
            <ListButton format="bulleted-list" label="≡" />
            <ListButton format="numbered-list" label="1." />
            <div className="h-16 w-px bg-border-default" />
            <LinkButton onMouseDown={handleLinkButtonMouseDown} />
          </div>

          <Editable
            className="max-h-[150px] min-h-96 overflow-x-hidden overflow-y-auto px-12 py-20 text-sm text-text-primary outline-none"
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            renderPlaceholder={renderPlaceholder}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            placeholder="Add guidance for QC users..."
          />
        </div>
      </Slate>

      {linkDialogOpen && (
        <div className="flex items-center gap-8 rounded-8 border border-border-default bg-canvas-subtle p-8">
          <div className="flex flex-1 flex-col gap-4">
            <input
              type="url"
              value={linkUrl}
              onChange={handleLinkUrlChange}
              onKeyDown={handleLinkInputKeyDown}
              placeholder="https://..."
              autoFocus
              className={`rounded border bg-canvas-default px-8 py-4 text-sm focus:ring-1 focus:outline-none ${linkUrl && !validURL ? 'border-border-danger focus:ring-border-danger' : 'border-border-default focus:ring-border-focus'}`}
            />
            {linkUrl && !validURL && <p className="text-xs">Enter a valid https:// URL</p>}
          </div>
          <button
            type="button"
            onClick={applyLink}
            disabled={linkUrl.length > 0 && !validURL}
            className="text-text-action text-sm hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            Apply
          </button>
          {linkActive && (
            <button type="button" onClick={removeLink} className="text-text-danger text-sm hover:underline">
              Remove
            </button>
          )}
          <button type="button" onClick={closeLinkDialog} className="text-text-tertiary">
            <PalmettoIcon name="x" size="xs" />
          </button>
        </div>
      )}

      {helperText && <p className="text-xs text-text-secondary">{helperText}</p>}
    </div>
  );
};
